import { ZeusHelpButton } from "@/components/zeus-help-dialog";
import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mic, Trophy, Loader2, HelpCircle, BookOpen } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function IncantationsPage() {
  const { toast } = useToast();
  const [content, setContent] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showExample, setShowExample] = useState(false);

  const { data: incantation, isLoading } = useQuery<{ content: string }>({
    queryKey: ["/api/incantation"],
  });

  const { data: stats } = useQuery<{ totalReadsThisMonth: number; monthName: string }>({
    queryKey: ["/api/incantation/stats"],
  });

  useEffect(() => {
    if (incantation && !hasLoaded) {
      setContent(incantation.content || "");
      setHasLoaded(true);
    }
  }, [incantation, hasLoaded]);

  const saveMutation = useMutation({
    mutationFn: async (text: string) => {
      return apiRequest("PUT", "/api/incantation", { content: text });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/incantation"] });
    },
  });

  const readMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/incantation/read", {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/incantation/stats"] });
      toast({
        title: "Read It!",
        description: "Great job reading your identity!",
      });
    },
  });

  const handleBlur = useCallback(() => {
    if (hasLoaded && content !== (incantation?.content || "")) {
      saveMutation.mutate(content);
    }
  }, [content, incantation, hasLoaded, saveMutation]);

  const totalReads = stats?.totalReadsThisMonth || 0;
  const monthName = stats?.monthName || new Date().toLocaleString("en-US", { month: "long" });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6 space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Mic className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold">Identity</h1>
        <Popover>
          <PopoverTrigger asChild>
            <button className="text-muted-foreground hover:text-foreground transition-colors">
              <HelpCircle className="w-5 h-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-sm leading-relaxed" side="bottom" align="start">
            <p>Enter a well planned statement of what you want in the future in the narrative style. Each day, start by opening the Identity and read it to yourself, when you're done click the box Read It. If you ever question your attitude, your energy, your happiness or your accomplishment of goals or wealth - check in to see how many times you are programming yourself for what you want.</p>
          </PopoverContent>
        </Popover>
        <div className="ml-auto flex items-center gap-2">
          <ZeusHelpButton focus="identity" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowExample(true)}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Example
          </Button>
        </div>
      </div>

      <Dialog open={showExample} onOpenChange={setShowExample}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">How to Create Your Daily Identity</DialogTitle>
            <DialogDescription>
              Based on the "6 Steps to Transmute Desire into Gold" from <em>Think and Grow Rich</em> by Napoleon Hill
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 text-sm leading-relaxed">
            <div>
              <h3 className="font-semibold text-base mb-2">The 6 Steps to Create Your Statement</h3>
              <ol className="list-decimal list-outside ml-5 space-y-2">
                <li><span className="font-medium">Define your goal</span> — State the precise amount or outcome you desire.</li>
                <li><span className="font-medium">Decide what you'll give</span> — Clearly state what you will offer in exchange for achieving this goal.</li>
                <li><span className="font-medium">Set a deadline</span> — Pick a specific date by which you intend to achieve your goal.</li>
                <li><span className="font-medium">Create your plan</span> — Develop a detailed plan and begin implementing it immediately, whether you feel ready or not.</li>
                <li><span className="font-medium">Write it down</span> — Summarize all four points above into one clear, concise written statement.</li>
                <li><span className="font-medium">Read it daily</span> — Read this statement aloud twice a day, once in the morning and once at night. As you read, visualize and <em>feel</em> the successful completion of your goal.</li>
              </ol>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-base mb-2">3 Keys for Success</h3>
              <ul className="space-y-2">
                <li className="flex gap-2">
                  <span className="font-medium text-primary shrink-0">Emotion & Faith:</span>
                  <span>Incorporate genuine emotion and belief into your statement. This is essential for influencing your subconscious mind.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-primary shrink-0">Visualization:</span>
                  <span>Actively see yourself having already achieved your goal each time you read your statement.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-primary shrink-0">Persistence:</span>
                  <span>Consistent daily repetition is key. Continue reading your identity every day until your goal is realized.</span>
                </li>
              </ul>
            </div>

            <div className="border-t pt-4 bg-muted/50 rounded-lg p-4">
              <h3 className="font-semibold text-base mb-2">Example Statement</h3>
              <p className="italic text-muted-foreground">
                "By [date], I will have [specific goal/amount]. In return, I will provide [what you will give — your service, effort, or value]. I believe with absolute faith that this is already on its way to me. I can see it, I can feel it, and I am taking action every single day to make it a reality."
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Daily Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <AutoExpandingTextarea
            placeholder="Write your daily identity here... Speak your truth into existence."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleBlur}
            minRows={8}
            className="text-base leading-relaxed"
            data-testid="incantation-textarea"
          />
          {saveMutation.isPending && (
            <p className="text-sm text-muted-foreground">Saving...</p>
          )}

          <div className="flex flex-col items-center gap-6 pt-4">
            <Button
              size="lg"
              onClick={() => readMutation.mutate()}
              disabled={readMutation.isPending || !content.trim()}
              className="px-12 py-6 text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="read-it-button"
            >
              {readMutation.isPending ? (
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
              ) : (
                <Mic className="w-6 h-6 mr-2" />
              )}
              Read It
            </Button>

            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-xl border">
              <Trophy className="w-8 h-8 text-yellow-500" />
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">In the month of {monthName}, you have completed</p>
                <p className="text-3xl font-bold" data-testid="success-count">{totalReads}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
